import { useNavigate } from 'react-router';
import { routes } from 'wasp/client/router';
import { UserAvatar, UserFlairBadge } from '../../components/common';
import type { DiscussionAuthor } from '../types';

interface AuthorBylineProps {
  user: DiscussionAuthor;
  date: Date;
  showAvatar?: boolean;
  showYear?: boolean;
  className?: string;
}

export function AuthorByline({
  user,
  date,
  showAvatar = false,
  showYear = false,
  className = '',
}: AuthorBylineProps) {
  const navigate = useNavigate();
  const dateOptions: Intl.DateTimeFormatOptions = showYear
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric' };

  const isLinkable = user.id !== 'deleted' && user.username !== null;

  const handleProfileClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (!isLinkable) return;
    if ('key' in e && e.key !== 'Enter' && e.key !== ' ') return;
    e.stopPropagation();
    e.preventDefault();
    navigate(
      routes.ProfileRoute.build({ params: { username: user.username! } }),
    );
  };

  const displayName = user.username || 'Anonymous';

  const nameElement = isLinkable ? (
    <span
      role="link"
      tabIndex={0}
      onClick={handleProfileClick}
      onKeyDown={handleProfileClick}
      aria-label={`View ${displayName}'s profile`}
      className="cursor-pointer font-medium text-neutral-700 transition-colors hover:text-primary hover:underline dark:text-neutral-200 dark:hover:text-primary"
    >
      {displayName}
    </span>
  ) : (
    <span className="font-medium text-neutral-700 dark:text-neutral-200">
      {displayName}
    </span>
  );

  const avatarElement = showAvatar ? (
    isLinkable ? (
      <span
        role="link"
        tabIndex={-1}
        onClick={handleProfileClick}
        onKeyDown={handleProfileClick}
        className="cursor-pointer"
        aria-hidden="true"
      >
        <UserAvatar username={user.username} size="md" />
      </span>
    ) : (
      <UserAvatar username={user.username} size="md" />
    )
  ) : null;

  return (
    <div
      className={`flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 ${className}`}
    >
      {avatarElement}
      <div className="flex items-center gap-1.5">
        {nameElement}
        <UserFlairBadge flair={user.flair?.tier} variant="compact" />
        <span className="text-neutral-300 dark:text-neutral-600">&middot;</span>
        <time>{new Date(date).toLocaleDateString('en-US', dateOptions)}</time>
      </div>
    </div>
  );
}
